import { useExportContext } from '@/contexts/ExportContext';
import { showDownloadedContent } from '@/lib/export-helpers';
import { useApiConfig } from '@/providers/ApiConfigProvider';
import { ExportFormat, type ColumnInput, type Forms } from '@/types/export';
import { useCallback, useState } from 'react';

const FIRST_RETRY_INTERVAL = 3000;
const BASE_RETRY_DELAY = 5000;
const MAX_RETRY_COUNT = 15;

interface UseExportAllOptions {
  form: Forms;
  getColumns: () => ColumnInput[];
  getFilter?: () => Record<string, unknown> | undefined;
  getSummary?: () => string | undefined;
  fileName?: string;
  onFinished?: () => void;
}

export function useExportAll({
  form,
  getColumns,
  getFilter,
  getSummary,
  fileName,
  onFinished,
}: UseExportAllOptions) {
  const { createExportJob } = useExportContext();
    const { apiBaseUrl, getHeaders } = useApiConfig();
  const [message, setMessage] = useState('Generating file. Please wait . . .');
  const [downloadHasFailed, setDownloadHasFailed] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const triggerExportAll = useCallback(async () => {
    setIsExporting(true);
    setDownloadHasFailed(false);
    setMessage('Generating file. Please wait . . .');

    try {
      const filter = getFilter?.();
      const summary = getSummary?.();
      const filterWithSummary = summary ? { ...(filter ?? {}), summary } : filter;

      const result = await createExportJob({
        export: {
          format: ExportFormat.Xlsx,
          form,
          columns: getColumns(),
          filter: filterWithSummary ? JSON.stringify(filterWithSummary) : null,
        },
      });

      if (!result?.id) {
        setDownloadHasFailed(true);
        setMessage('Export has failed!');
        setIsExporting(false);
        return;
      }

      const receiptId = result.id;
      let retryCount = 0;

      const downloadExportFileIfReady = async () => {
        try {
          const url = new URL(`${apiBaseUrl}/api/v1/exports/${receiptId}`);

          const headers = await getHeaders();
          const response = await fetch(url.toString(), {
            method: 'GET',
            headers,
          });

          if (response.status === 200) {
            const blob = await response.blob();
            showDownloadedContent(blob, fileName ?? crypto.randomUUID());
            setIsExporting(false);
            onFinished?.();
          } else if ((response.status === 204 || response.status === 404) && retryCount < MAX_RETRY_COUNT) {
            retryCount++;
            const nextRetryDelay = BASE_RETRY_DELAY * retryCount;
            setTimeout(downloadExportFileIfReady, nextRetryDelay);
          } else {
            setDownloadHasFailed(true);
            setMessage('Export has failed!');
            setIsExporting(false);
          }
        } catch {
          setDownloadHasFailed(true);
          setMessage('Export has failed!');
          setIsExporting(false);
        }
      };

      setTimeout(downloadExportFileIfReady, FIRST_RETRY_INTERVAL);
    } catch {
      setDownloadHasFailed(true);
      setMessage('Export has failed!');
      setIsExporting(false);
    }
  }, [createExportJob, form, getColumns, getFilter, getSummary, fileName, onFinished, apiBaseUrl, getHeaders]);

  return { downloadHasFailed, message, isExporting, triggerExportAll };
}
